import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { createRevenueCatProduct } from '../../graphql/mutation';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const PRODUCT_TYPES = [
  'WEEKLY',
  'MONTHLY', 
  'YEARLY',
  'INSTANT',
  'ONE_TIME',
  'PROMO'
];

export default function CreateRevenueCatProduct() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    startDate: new Date().toISOString().slice(0, 16),
    endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    limit: '',
    price: '',
    type: ''
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [loading, setLoading] = useState(false);

  const [createProduct] = useMutation(createRevenueCatProduct);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || '' : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.amount || !formData.startDate || !formData.endDate || !formData.limit || !formData.price || !formData.type) {
      setSnackbar({
        open: true,
        message: 'Please fill in all fields',
        severity: 'error'
      });
      return;
    }

    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      setSnackbar({
        open: true,
        message: 'Start date must be before end date',
        severity: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const response = await createProduct({
        variables: {
          input: {
            name: formData.name,
            amount: parseInt(formData.amount),
            startDate: new Date(formData.startDate),
            endDate: new Date(formData.endDate),
            limit: parseInt(formData.limit),
            price: parseFloat(formData.price),
            type: formData.type
          }
        }
      });

      if (response.data) {
        setSnackbar({
          open: true,
          message: 'Product created successfully!',
          severity: 'success'
        });
        
        setTimeout(() => {
          navigate('/revenuecat-products');
        }, 1500);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to create product',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 4 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/revenuecat-products')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" fontWeight="bold">
          Create RevenueCat Product
        </Typography>
      </Box>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., Weekly Premium Plan"
                helperText="Enter a descriptive name for the product"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Product Type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                helperText="Select the product category"
              >
                {PRODUCT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Credit Amount"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleChange}
                required
                inputProps={{ min: 1, step: 1 }}
                placeholder="e.g., 60"
                helperText="Number of credits to give"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price ($)"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleChange}
                required
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="e.g., 9.99"
                helperText="Product price in USD"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Daily Limit"
                name="limit"
                type="number"
                value={formData.limit}
                onChange={handleChange}
                required
                inputProps={{ min: 1, step: 1 }}
                placeholder="e.g., 10"
                helperText="Maximum credits per day"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                name="startDate"
                type="datetime-local"
                value={formData.startDate}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
                helperText="When the product becomes available"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="End Date"
                name="endDate"
                type="datetime-local"
                value={formData.endDate}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
                helperText="When the product expires"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/revenuecat-products')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Creating...' : 'Create Product'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
